import { useTranslation } from "react-i18next"

import type { ScorecardDimension } from "../types"

import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"

import { EvidenceCell } from "./evidence-cell"

function scoreColor(score: number): string {
  if (score >= 75) return "bg-success-ink"
  if (score >= 50) return "bg-warning-ink"
  return "bg-destructive-ink"
}

function scoreLabelColor(score: number): string {
  if (score >= 75) return "text-success"
  if (score >= 50) return "text-warning"
  return "text-destructive"
}

function scoreStrokeColor(score: number): string {
  if (score >= 75) return "text-success"
  if (score >= 50) return "text-warning"
  return "text-destructive"
}

type DimensionRowProps = {
  dim: ScorecardDimension
}

function DimensionRow({ dim }: DimensionRowProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{dim.label}</span>
        <div className="flex items-center gap-2">
          {dim.evidence && <EvidenceCell evidence={dim.evidence} />}
          <span className={cn("text-sm font-bold tabular-nums", scoreLabelColor(dim.score))}>
            {dim.score}%
          </span>
        </div>
      </div>
      <Progress className="h-2" indicatorClassName={scoreColor(dim.score)} value={dim.score} />
      {dim.description && <p className="text-muted-foreground text-xs">{dim.description}</p>}
    </div>
  )
}

type ScorecardProps = {
  dimensions: ScorecardDimension[]
  overallScore: number
}

export function Scorecard({ dimensions, overallScore }: ScorecardProps) {
  const { t } = useTranslation()
  return (
    <div className="space-y-5">
      {/* Overall score */}
      <div className="flex items-center gap-6">
        <div className="relative flex size-20 shrink-0 items-center justify-center">
          <svg className="-rotate-90" height="80" viewBox="0 0 80 80" width="80">
            <circle
              className="text-muted stroke-current"
              cx="40"
              cy="40"
              fill="none"
              r="34"
              strokeWidth="8"
            />
            <circle
              cx="40"
              cy="40"
              fill="none"
              r="34"
              strokeDasharray={`${(overallScore / 100) * 213.6} 213.6`}
              strokeLinecap="round"
              strokeWidth="8"
              className={cn(
                "stroke-current transition-[stroke-dasharray] duration-700",
                scoreStrokeColor(overallScore)
              )}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={cn("text-xl font-bold tabular-nums", scoreLabelColor(overallScore))}>
              {overallScore}
            </span>
            <span className="text-muted-foreground text-[10px]">/ 100</span>
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold">
            {t("tender.sections.recommendation.overallScore")}
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {t("tender.sections.recommendation.weightedAverage")}
          </p>
        </div>
      </div>

      {/* Dimension bars — labels are clickable when evidence exists */}
      <div className="space-y-3">
        {dimensions.map((dim) => (
          <DimensionRow key={dim.id} dim={dim} />
        ))}
      </div>
    </div>
  )
}
